/**
 * Pagination Component Styled Elements
 *
 * Styled components for the pagination component.
 *
 * @since 1.0.0
 */

import styled from "@emotion/styled";

import { colors } from "../../colors";

/**
 * Pagination container
 *
 * @since 1.0.0
 */
export const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 0;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
`;

/**
 * Page info text
 *
 * @since 1.0.0
 */
export const PageInfo = styled.div`
  color: ${colors.gray[800]};
  font-size: 1em;
  white-space: nowrap;

  @media (max-width: 768px) {
    order: 3;
  }
`;

/**
 * Navigation container
 *
 * @since 1.0.0
 */
export const NavigationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

/**
 * Page button
 *
 * @since 1.0.0
 */
export const PageButton = styled.button<{
  $isActive?: boolean;
  $disabled?: boolean;
}>`
  min-width: 32px;
  height: 32px;
  padding: 4px 8px;
  border: 1px solid
    ${({ $isActive }) => ($isActive ? colors.primary[300] : colors.gray[100])};
  background: ${({ $isActive }) =>
    $isActive ? colors.primary[300] : colors.white};
  color: ${({ $isActive }) => ($isActive ? colors.white : colors.gray[800])};
  font-size: 1em;
  border-radius: 3px;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${({ $isActive }) =>
      $isActive ? colors.primary[300] : colors.primary[50]};
    color: ${({ $isActive }) =>
      $isActive ? colors.white : colors.primary[300]};
    border-color: ${colors.primary[300]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus {
    outline: 1px solid ${colors.primary[300]};
    outline-offset: -1px;
    box-shadow: none !important;
  }
`;

/**
 * Ellipsis indicator
 *
 * @since 1.0.0
 */
export const Ellipsis = styled.span`
  padding: 4px 8px;
  color: ${colors.gray[500]};
  font-size: 1em;
`;

/**
 * Items per page container
 *
 * @since 1.0.0
 */
export const ItemsPerPageContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1em;
  color: ${colors.gray[800]};

  @media (max-width: 768px) {
    order: 1;
  }
`;

/**
 * Items per page select
 *
 * @since 1.0.0
 */
export const ItemsPerPageSelect = styled.select`
  padding: 4px 8px;
  border: 1px solid ${colors.gray[200]};
  border-radius: 3px;
  background: ${colors.white};
  font-size: 1em;
  cursor: pointer;
  color: ${colors.gray[800]};

  &:focus {
    outline: 1px solid ${colors.primary[300]};
    outline-offset: -1px;
    box-shadow: none !important;
  }
`;
